import DOMPurify from 'dompurify';

/**
 * 클라이언트 HTML 새니타이저 (XSS 방어 2중화)
 *
 * - 서버 저장 시 backend/utils/sanitize.ts (sanitize-html) 로 이미 정제되지만,
 *   서버 통과 전/후 모든 렌더 경로에서 한번 더 필터해 공격 표면을 최소화한다.
 * - 허용 규칙은 서버 화이트리스트와 최대한 일치시킴.
 * - img src 는 반드시 /uploads/ 로 시작해야 함 (uponSanitizeAttribute 훅)
 * - a href 는 http/https 만 허용, 자동으로 target=_blank + rel=noopener noreferrer nofollow
 */

const ALLOWED_TAGS = [
  'p', 'br', 'h2', 'h3', 'blockquote', 'hr',
  'ul', 'ol', 'li',
  // 표 — 시황 리포트(국장/미장/장중/모닝/코인) 데이터 표시용
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  'strong', 'em', 'u', 's', 'code',
  // 인라인 색상 강조 — span.up / span.down (등락률 색상)
  'span',
  'a', 'img',
];

const ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'alt', 'title', 'width', 'height', 'class'];

/** DOMPurify 훅은 전역 1회만 등록 */
let hooksRegistered = false;
function ensureHooks() {
  if (hooksRegistered) return;
  hooksRegistered = true;

  // img src 가 /uploads/ 로 시작하지 않으면 속성 제거 → 결국 빈 img 됨
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    const name = data.attrName.toLowerCase();
    const value = (data.attrValue ?? '').trim();

    if (node.nodeName === 'IMG' && name === 'src') {
      if (!value.startsWith('/uploads/')) {
        data.keepAttr = false;
      }
    }

    if (node.nodeName === 'A' && name === 'href') {
      // http(s) 외부 링크 또는 사이트 내부 절대 경로(/post/.../market/...) 허용.
      // 사이트 내부 경로는 SPA 라우터가 SPA 이동, 외부는 새 탭으로 분기 (afterSanitize 훅).
      const isExternal = /^https?:\/\//i.test(value);
      const isInternal = value.startsWith('/');
      if (!isExternal && !isInternal) {
        data.keepAttr = false;
      }
    }
  });

  // a 태그에 안전 속성 강제 — 외부만 새 탭/noopener, 내부 경로는 SPA 이동 위해 target 없음.
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName === 'A') {
      const href = (node as HTMLAnchorElement).getAttribute('href') ?? '';
      if (href.startsWith('http://') || href.startsWith('https://')) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer nofollow');
      } else if (href.startsWith('/')) {
        // 내부 경로 — target/rel 제거 (기존에 들어있을 수 있음)
        node.removeAttribute('target');
        node.removeAttribute('rel');
      }
    }
    // src 없는 img 는 제거
    if (node.nodeName === 'IMG' && !(node as HTMLImageElement).getAttribute('src')) {
      node.parentNode?.removeChild(node);
    }
  });
}

export function sanitizePostHtml(dirty: string): string {
  ensureHooks();
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    // 추가 보안: self-XSS 페이로드 흔적 제거
    FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input', 'object', 'embed'],
    FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick'],
  });
}

/* ══════════════════════════════════════════════════════
 *  가이던스 봇(자동글) 전용 sanitizer
 *  - 신뢰 경계: posts.auto_source === 'guidance-bot' 일 때만 호출
 *  - 서버 backend/utils/sanitize.ts:sanitizeGuidanceHtml 화이트리스트와 짝맞춤
 *  - <style> 블록 + SVG 차트 + inline style 허용 (인포그래픽 렌더 필수)
 *  - script/iframe/form/input/object/embed 차단 유지
 *  - on* 이벤트 핸들러 전면 차단 유지 (FORBID_ATTR + ALLOWED_ATTR 양쪽 보호)
 *  - href javascript:/data: 차단 (sanitizePostHtml 의 uponSanitizeAttribute 훅 재사용)
 * ══════════════════════════════════════════════════════ */

const GUIDANCE_ALLOWED_TAGS = [
  // 블록/구조
  'div', 'p', 'br', 'h1', 'h2', 'h3', 'h4', 'blockquote', 'hr',
  // 리스트
  'ul', 'ol', 'li',
  // 표
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  // 인라인
  'span', 'strong', 'em', 'i', 'u', 's', 'code',
  // 링크 (uponSanitizeAttribute 훅에서 http/https + 내부 경로만 통과)
  'a',
  // 스타일 블록 (인포그래픽 핵심 — CSS 정의)
  'style',
  // SVG (차트) — 서버 화이트리스트와 동일
  'svg', 'g', 'rect', 'line', 'text', 'title', 'desc',
  'path', 'polyline', 'polygon', 'circle', 'ellipse',
  'defs', 'lineargradient', 'radialgradient', 'stop', 'clippath',
] as const;

/* SVG/MathML 속성은 case-sensitive (viewBox, preserveAspectRatio 등).
 * DOMPurify 의 ALLOWED_ATTR 비교는 lowercase. 키워드를 lowercase 로 줘야 매칭됨.
 * 단, 출력 직전 afterSanitizeElements 훅에서 viewbox -> viewBox 로 복원해
 * 브라우저가 SVG 좌표계를 인식하게 한다 (서버 lowerCaseAttributeNames:false 와 동일 효과). */
const GUIDANCE_ALLOWED_ATTR = [
  // 공통
  'class', 'id', 'role', 'aria-label', 'style',
  // a
  'href', 'target', 'rel',
  // SVG 공통 좌표/색상
  'viewbox', 'xmlns',
  'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry',
  'width', 'height',
  'd', 'points',
  'fill', 'stroke', 'stroke-width', 'stroke-dasharray', 'opacity',
  'text-anchor', 'dominant-baseline', 'font-size', 'font-weight', 'font-family',
  'transform',
  // gradient/clip
  'offset', 'stop-color', 'stop-opacity',
  // 표
  'colspan', 'rowspan',
  // img (인포그래픽이 사용할 가능성)
  'src', 'alt', 'title',
];

/* SVG 속성 케이스 복원 매핑 (DOMPurify 통과 후 후처리) */
const SVG_CASE_RESTORE: Record<string, string> = {
  viewbox: 'viewBox',
  preserveaspectratio: 'preserveAspectRatio',
  stopcolor: 'stop-color',  // 보호: 일부 빌드는 케밥으로 들어옴
  stopopacity: 'stop-opacity',
  textanchor: 'text-anchor',
  dominantbaseline: 'dominant-baseline',
  strokewidth: 'stroke-width',
  strokedasharray: 'stroke-dasharray',
  fontsize: 'font-size',
  fontweight: 'font-weight',
  fontfamily: 'font-family',
};

let guidanceHooksRegistered = false;
function ensureGuidanceHooks() {
  if (guidanceHooksRegistered) return;
  guidanceHooksRegistered = true;

  /* 1) on* 이벤트 핸들러는 어떤 경로로도 통과 못 하게 한 번 더 방어
   *    (FORBID_ATTR 와 중복이지만 의도적으로 명시) */
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    const name = (data.attrName ?? '').toLowerCase();
    if (name.startsWith('on')) {
      data.keepAttr = false;
      return;
    }
    /* href 스킴 검사 — javascript:/data: 차단 */
    if (name === 'href') {
      const v = (data.attrValue ?? '').trim();
      const isExternal = /^https?:\/\//i.test(v);
      const isInternal = v.startsWith('/');
      if (!isExternal && !isInternal) data.keepAttr = false;
    }
    /* style 속성 안의 expression()/url(javascript:) 차단 */
    if (name === 'style') {
      const v = (data.attrValue ?? '').toLowerCase();
      if (v.includes('expression(') || v.includes('javascript:') || v.includes('vbscript:')) {
        data.keepAttr = false;
      }
    }
  });

  /* 2) SVG 속성 케이스 복원 — viewbox -> viewBox 등.
   *    DOMPurify 가 lowercase 화이트리스트로 통과시킨 뒤 원래 케이스로 되돌린다.
   *    이걸 안 하면 브라우저가 viewbox 를 무시해 SVG 차트 좌표계가 깨짐. */
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (!(node as Element).attributes) return;
    const attrs = (node as Element).attributes;
    for (let i = attrs.length - 1; i >= 0; i--) {
      const attr = attrs[i];
      const lower = attr.name.toLowerCase();
      const restored = SVG_CASE_RESTORE[lower];
      if (restored && restored !== attr.name) {
        const v = attr.value;
        (node as Element).removeAttribute(attr.name);
        (node as Element).setAttribute(restored, v);
      }
    }
  });

  /* 3) style 블록 안의 CSS 도 expression/javascript: 패턴 후처리 (이중 방어).
   *    템플릿이 생성하는 CSS 는 고정이지만 일반화된 자동글 안전망. */
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName === 'style') {
      const text = (node as Element).textContent ?? '';
      const lower = text.toLowerCase();
      if (lower.includes('expression(') || lower.includes('javascript:') || lower.includes('vbscript:')) {
        (node as Element).textContent = '';
      }
    }
  });
}

export function sanitizeGuidancePostHtml(dirty: string): string {
  ensureGuidanceHooks();
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: GUIDANCE_ALLOWED_TAGS as unknown as string[],
    ALLOWED_ATTR: GUIDANCE_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    /* 이벤트 핸들러 + 위험 태그는 화이트리스트에 없어도 명시 차단 (이중 방어) */
    FORBID_TAGS: ['script', 'iframe', 'form', 'input', 'object', 'embed', 'meta', 'link'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange'],
    /* <style> 블록을 유지하기 위한 필수 옵션.
     *  - DOMPurify 는 fragment 입력에서 <style> 을 자동 제거함 (HEAD 요소로 인식).
     *  - FORCE_BODY:true 가 input 을 <body> 로 감싸 <style> 을 body 자식으로 유지하게 함.
     *  - WHOLE_DOCUMENT 는 false 유지 (html/head 까지 반환되면 다른 SPA 렌더에 영향). */
    WHOLE_DOCUMENT: false,
    FORCE_BODY: true,
    /* SVG 가 namespace 검사를 통과하도록 (DOMPurify 6+ 기본 true 지만 명시) */
    USE_PROFILES: { html: true, svg: true, svgFilters: false, mathMl: false },
  });
}

/**
 * 순수 텍스트 추출 (bio/제목 안전 표시용)
 */
export function stripHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

/**
 * Markdown-lite → HTML 변환 (게시글 렌더 직전 단계)
 *
 * 공시/뉴스 자동 생성 포스트는 Claude API가 반환한 Markdown 원문이
 * `<p>${summary}</p>` 로 그대로 감싸져 저장됨 → HTML 렌더 시 `\n\n`, `# 제목`,
 * `**bold**` 가 그대로 표시되어 한 줄로 붙어 보임.
 *
 * 마크다운 흔적이 감지될 때만 동작 (일반 유저 포스트는 무변경).
 * 출력 태그는 `<p>`, `<h3>`, `<strong>`, `<br>` 로 sanitize 화이트리스트 내.
 */
export function renderMarkdownLite(html: string): string {
  const hasMarkdown = /\n\n|(^|\n)#\s|\*\*[^*\n]+\*\*|기업:\s/m.test(html);
  if (!hasMarkdown) return html;

  // 1) **강조** → <strong>
  let out = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');

  // 2) <p>…</p> 내부를 이중 개행으로 분할해 재구성 — 속성 있는 <p ...> 도 매칭
  out = out.replace(/<p(?:\s[^>]*)?>([\s\S]*?)<\/p>/g, (_m, inner: string) => {
    const blocks = inner
      .split(/\n{2,}/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (blocks.length === 0) return '';
    return blocks
      .map((block) => {
        // `# 제목` 또는 `## 제목` → <h3>
        const h = block.match(/^#{1,6}\s+(.+)$/);
        if (h) return `<h3>${h[1].trim()}</h3>`;
        // 단일 개행 → <br>
        return `<p>${block.replace(/\n/g, '<br>')}</p>`;
      })
      .join('');
  });

  // 3) 메타 단락 `기업: … · 종목코드: … · 접수일: YYYYMMDD`
  //    — `기업:` 항목 제거, `접수일` 날짜 포맷(YYYY.MM.DD), blockquote 로 감싸 구조 구분
  out = out.replace(/<p(?:\s[^>]*)?>(기업:[^<]+)<\/p>/g, (_m, inner: string) => {
    const parts = inner
      .split(/\s*·\s*/)
      .map((s) => s.trim())
      .filter(Boolean);
    const kept = parts
      .filter((p) => !/^기업:/.test(p))
      .map((p) => {
        const m = p.match(/^접수일:\s*(\d{4})(\d{2})(\d{2})$/);
        return m ? `접수일: ${m[1]}.${m[2]}.${m[3]}` : p;
      });
    if (kept.length === 0) return '';
    return `<blockquote>${kept.join('<br>')}</blockquote>`;
  });

  // 4) 한국어 문장 종결(…다./…요.) 뒤에 <br> 삽입 — 공시/뉴스 요약문 가독성
  //    `\s+` 가 있어야 매칭되므로 단락/태그 끝(</p>, </strong>)에선 매칭되지 않음
  out = out.replace(/([다요])\.\s+/g, '$1.<br>');

  // 5) `<br><strong>…</strong>` 직전에 단락 경계 삽입
  //    — 요약 본문과 강조 경고문(`**투자자는…**`) 사이 여백 분리
  out = out.replace(/<br>(<strong>[^<]+<\/strong>)/g, '</p><p>$1');

  // 6) 원문 보기 링크 — `📄 원문 보기 →` 등 이모지/화살표 제거, 텍스트만 남김
  out = out.replace(
    /(<a\b[^>]*>)\s*(?:📄\s*)?원문\s*보기\s*(?:→|->)?\s*(<\/a>)/g,
    '$1원문 보기$2',
  );

  return out;
}
